import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/common";
import { useCollection } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

const PRIO = { high: "text-destructive", medium: "text-amber", low: "text-sage" };

export default function Todo() {
  const { rows, create, update, remove } = useCollection("todos");
  const [title, setTitle] = useState("");
  const [prio, setPrio] = useState("medium");
  const add = async (e) => { e.preventDefault(); if (!title.trim()) return; await create({ title, priority: prio, done: false }); setTitle(""); };
  const openItems = rows.filter((t) => !t.done), doneItems = rows.filter((t) => t.done);

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader eyebrow="Workspace" title="To-do" description="Your personal desk. Nobody else sees this list." />
      <form onSubmit={add} className="paper-card p-3 flex gap-2 rise">
        <Input data-testid="todo-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Write it down…" className="bg-transparent border-0 focus-visible:ring-0 text-base" />
        <div className="flex gap-1">{["low", "medium", "high"].map((p) => <button type="button" key={p} data-testid={`todo-priority-${p}`} onClick={() => setPrio(p)} className={cn("px-2 rounded-full text-[10px] font-mono uppercase border transition-colors", prio === p ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground")}>{p}</button>)}</div>
        <Button type="submit" data-testid="todo-add-button" className="rounded-full"><Plus size={16} /></Button>
      </form>
      <div className="paper-card overflow-hidden rise" data-testid="todo-list">
        {openItems.length === 0 && doneItems.length === 0 && <EmptyState title="A clear desk" hint="Add your first item above." />}
        {openItems.map((t) => <Item key={t.id} t={t} update={update} remove={remove} />)}
        {doneItems.length > 0 && <div className="px-4 py-2 eyebrow !text-muted-foreground bg-secondary/40 border-y border-border">Done · {doneItems.length}</div>}
        {doneItems.map((t) => <Item key={t.id} t={t} update={update} remove={remove} />)}
      </div>
    </div>
  );
}

function Item({ t, update, remove }) {
  return (
    <div className="ledger-row group" style={{ gridTemplateColumns: "auto 1fr auto auto" }} data-testid={`todo-item-${t.id}`}>
      <Checkbox checked={!!t.done} data-testid={`todo-toggle-${t.id}`} onCheckedChange={(v) => update(t.id, { done: !!v })} className="rounded-full h-5 w-5" />
      <span className={cn("text-sm transition-colors", t.done && "line-through text-muted-foreground")}>{t.title}</span>
      <span className={cn("font-mono text-[10px] uppercase", PRIO[t.priority])}>{t.priority}</span>
      <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive" data-testid={`todo-delete-${t.id}`} onClick={() => remove(t.id)}><Trash2 size={12} /></Button>
    </div>
  );
}
